import React, { useState } from "react";
import { Avatar, Box, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Button from "components/basic/button";
import ConfirmDialog from "components/basic/confirmDialog";
import Modal from "components/basic/modal";
import Typography from "components/basic/typography";
import { useSnack } from "hooks/useSnack";

import ProfileRow from "../../../ProfileRow";
import { useAdminActions } from "../../../../adminApi";
import { formatDate } from "../../../../helpers";
import { AdminUser } from "../../../../types";

interface UserDetailProps {
  user: AdminUser;
  onChanged: () => void;
  onClose: () => void;
}

const UserDetail: React.FC<UserDetailProps> = ({
  user,
  onChanged,
  onClose,
}) => {
  const showSnack = useSnack();
  const { setAccountSuspended } = useAdminActions();
  const [busy, setBusy] = useState(false);
  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const { account } = user;
  const suspended = Boolean(user.suspendedAt);

  const setSuspended = async (next: boolean) => {
    setBusy(true);
    const result = await setAccountSuspended(user.id, next);
    setBusy(false);
    setConfirmSuspend(false);
    if (!result.ok) {
      showSnack(result.error ?? "Failed to update account", "error");
      return;
    }
    showSnack(next ? "Account suspended" : "Account restored", "success");
    onChanged();
  };

  return (
    <Modal onClose={onClose}>
      <Box sx={{ width: { xs: "min(340px, 100vw - 64px)", sm: 520 } }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Avatar
            src={account.profilePhotoUrl ?? undefined}
            imgProps={{ referrerPolicy: "no-referrer" }}
            sx={{ width: 56, height: 56 }}
          />
          <Box flexGrow={1} minWidth={0}>
            <Typography variant="h4" style={{ fontWeight: "bold" }}>
              {account.firstName} {account.lastName}
            </Typography>
            <Typography variant="body2">{account.email}</Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>

        <ProfileRow label="Phone" value={account.phoneNumber} />
        <ProfileRow label="Address" value={account.address} />
        <ProfileRow
          label="Signs in with"
          value={account.authProvider ?? "Magic link"}
        />
        <ProfileRow
          label="Came from"
          value={
            user.portalVendorName
              ? `${user.portalVendorName}'s portal`
              : "Marketplace"
          }
        />
        <ProfileRow label="Joined" value={formatDate(account.createdAt)} />
        {suspended && (
          <ProfileRow
            label="Suspended"
            value={new Date(user.suspendedAt!).toLocaleString("en-CA")}
          />
        )}

        {!user.isAdmin && (
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button
              size="small"
              color={suspended ? "primary" : "error"}
              loading={busy && !confirmSuspend}
              disabled={busy}
              onClick={() =>
                suspended ? setSuspended(false) : setConfirmSuspend(true)
              }
            >
              {suspended ? "Restore account" : "Suspend account"}
            </Button>
          </Box>
        )}

        {confirmSuspend && (
          <ConfirmDialog
            title="Suspend this account?"
            body="They're signed out everywhere and can't sign back in until you restore the account."
            confirmLabel="Suspend"
            destructive
            loading={busy}
            onConfirm={() => setSuspended(true)}
            onClose={() => setConfirmSuspend(false)}
          />
        )}
      </Box>
    </Modal>
  );
};

export default UserDetail;
